/* ============================================================================
   sanitize-html.js - allowlist HTML sanitizer for FAQ answers + legal CMS bodies
   ============================================================================ */

const FORBIDDEN_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "option",
  "link",
  "meta",
  "base",
  "svg",
  "math",
  "template",
  "noscript",
  "video",
  "audio",
  "source",
  "track",
  "canvas",
  "applet",
  "frame",
  "frameset",
]);

const FAQ_TAGS = new Set(["p", "br", "strong", "b", "em", "i", "ul", "ol", "li", "a", "code"]);

const LEGAL_TAGS = new Set([
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "p",
  "br",
  "hr",
  "strong",
  "b",
  "em",
  "i",
  "ul",
  "ol",
  "li",
  "a",
  "table",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "nav",
  "div",
  "section",
  "span",
  "blockquote",
  "code",
  "pre",
]);

const SAFE_HOSTS = new Set([
  "www.cookiecodejar.com",
  "cookiecodejar.com",
  "ko-fi.com",
  "www.ko-fi.com",
  "instagram.com",
  "www.instagram.com",
  "episodeinteractive.com",
  "www.episodeinteractive.com",
]);

/** Relative app page, in-page hash, or https to an allowlisted host. */
export function isSafeRichHref(href) {
  const value = String(href || "").trim();
  if (!value) return false;
  if (value.startsWith("#") && !value.startsWith("#javascript")) return true;
  if (/^[a-z0-9_\-./]+\.html(?:\?[^#]*)?(?:#.*)?$/i.test(value) && !value.includes("..")) {
    return true;
  }
  if (/^(javascript|data|vbscript|file):/i.test(value)) return false;
  try {
    const url = new URL(value, "https://www.cookiecodejar.com/");
    if (url.protocol !== "https:" && url.protocol !== "http:") return false;
    return SAFE_HOSTS.has(url.hostname.toLowerCase());
  } catch (_) {
    return false;
  }
}

function cleanClassName(value) {
  return String(value || "")
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => /^[a-zA-Z_][\w\-]*$/.test(part))
    .slice(0, 12)
    .join(" ");
}

function cleanId(value) {
  const id = String(value || "").trim();
  if (!/^[a-zA-Z][\w\-:.]{0,80}$/.test(id)) return "";
  return id;
}

function sanitizeElement(el, allowedTags) {
  const children = [...el.childNodes];
  for (const node of children) {
    if (node.nodeType === Node.COMMENT_NODE) {
      node.remove();
      continue;
    }
    if (node.nodeType === Node.TEXT_NODE) continue;
    if (node.nodeType !== Node.ELEMENT_NODE) {
      node.remove();
      continue;
    }

    const tag = node.tagName.toLowerCase();
    if (FORBIDDEN_TAGS.has(tag)) {
      node.remove();
      continue;
    }

    if (!allowedTags.has(tag)) {
      // Unwrap: keep children, drop the wrapper.
      while (node.firstChild) el.insertBefore(node.firstChild, node);
      node.remove();
      continue;
    }

    // Drop every attribute, then re-apply only safe ones.
    const rawAttrs = [...node.attributes].map((attr) => [attr.name, attr.value]);
    for (const attr of [...node.attributes]) node.removeAttribute(attr.name);

    for (const [name, value] of rawAttrs) {
      const attr = String(name || "").toLowerCase();
      if (attr.startsWith("on") || attr === "style" || attr === "srcdoc" || attr === "srcset") {
        continue;
      }
      if (attr === "class") {
        const cleaned = cleanClassName(value);
        if (cleaned) node.setAttribute("class", cleaned);
        continue;
      }
      if (attr === "id") {
        const cleaned = cleanId(value);
        if (cleaned) node.setAttribute("id", cleaned);
        continue;
      }
      if (tag === "a" && attr === "href") {
        if (isSafeRichHref(value)) node.setAttribute("href", String(value).trim());
        continue;
      }
      if (tag === "a" && attr === "target") {
        if (String(value).toLowerCase() === "_blank") node.setAttribute("target", "_blank");
        continue;
      }
      if (tag === "a" && attr === "rel") {
        // Rebuilt below when target=_blank.
        continue;
      }
      if (attr === "aria-label") {
        const label = String(value || "")
          .replace(/[<>]/g, "")
          .trim()
          .slice(0, 120);
        if (label) node.setAttribute("aria-label", label);
        continue;
      }
      if (attr === "aria-current") {
        const cur = String(value || "").toLowerCase();
        if (cur === "page" || cur === "true") node.setAttribute("aria-current", cur);
        continue;
      }
      if ((tag === "th" || tag === "td") && (attr === "colspan" || attr === "rowspan")) {
        const n = Number(value);
        if (Number.isInteger(n) && n >= 1 && n <= 20) node.setAttribute(attr, String(n));
        continue;
      }
      if ((tag === "th" || tag === "td") && attr === "scope") {
        const scope = String(value).toLowerCase();
        if (scope === "col" || scope === "row" || scope === "colgroup" || scope === "rowgroup") {
          node.setAttribute("scope", scope);
        }
      }
    }

    if (tag === "a") {
      if (node.getAttribute("target") === "_blank") {
        node.setAttribute("rel", "noopener noreferrer");
      } else {
        node.removeAttribute("rel");
      }
      if (!node.getAttribute("href")) {
        // Dead link: unwrap to text.
        while (node.firstChild) el.insertBefore(node.firstChild, node);
        node.remove();
        continue;
      }
    }

    sanitizeElement(node, allowedTags);
  }
}

/**
 * Allowlist sanitizer. Uses DOMParser (browser). Unknown tags are unwrapped;
 * forbidden tags are removed with their contents.
 */
export function sanitizeRichHtml(html, profile = "faq") {
  const raw = String(html || "");
  if (!raw.trim()) return "";
  if (typeof DOMParser === "undefined") return "";

  const allowedTags = profile === "legal" ? LEGAL_TAGS : FAQ_TAGS;
  const doc = new DOMParser().parseFromString(`<div id="ccj-sanitize-root">${raw}</div>`, "text/html");
  const root = doc.getElementById("ccj-sanitize-root");
  if (!root) return "";
  sanitizeElement(root, allowedTags);
  return root.innerHTML;
}

/** FAQ answers: small inline/block set. */
export function sanitizeFaqAnswerHtml(html) {
  return sanitizeRichHtml(html, "faq");
}

/** Legal CMS bodies: headings, lists, tables, site nav classes. */
export function sanitizeLegalHtml(html) {
  return sanitizeRichHtml(html, "legal");
}
